import { lazy, Show, Suspense, type ParentProps } from "solid-js"
import { createStore } from "solid-js/store"
import { createMediaQuery } from "@solid-primitives/media"
import { Titlebar, type TitlebarUpdate } from "@/shell/titlebar/titlebar"
import { usePlatform } from "@/runtime/platform/platform"
import { ToastRegion } from "@/shell/notifications/toast"
import { UploadToastHost } from "@/composer/attachments/uploads"
import { TitlebarRightProvider } from "@/shell/titlebar/right-slot"
import { useSettingsSurface } from "@/settings/surface"
import { useSettings } from "@/settings/model"
import { SshAuthentication } from "@/servers/ssh/authentication"
import { useUpdaterInstall } from "@/shell/updates/download"
import { useCommand } from "@/shell/commands/command"
import { useLanguage } from "@/runtime/i18n/language"
import "./workbench.css"

const DebugBar = lazy(() => import("@/shell/debug/debug-bar").then((module) => ({ default: module.DebugBar })))

export default function Layout(props: ParentProps) {
  const platform = usePlatform()
  const settings = useSettingsSurface()
  const preferences = useSettings()
  const installUpdate = useUpdaterInstall()
  const command = useCommand()
  const language = useLanguage()
  const mobile = createMediaQuery("(max-width: 767px)")
  const compactNavigation = createMediaQuery("(max-width: 959px)")
  const [state, setState] = createStore({
    debugTools: false,
    navigationMount: undefined as HTMLElement | undefined,
  })
  const expandedNavigation = () => preferences.appearance.sessionSidebar() && !compactNavigation()
  const bottomTitlebar = () => mobile() && preferences.general.mobileTitlebarPosition() === "bottom"

  const update: TitlebarUpdate = {
    get state() {
      return platform.updater?.state()
    },
    install: installUpdate,
  }
  command.register("debug-bar", () => [
    {
      id: "debugBar.toggle",
      title: language.t("command.debugBar.toggle"),
      category: language.t("command.category.view"),
      onSelect: () => setState("debugTools", (value) => !value),
    },
  ])

  return (
    <TitlebarRightProvider>
      <div
        data-component="sair-workbench"
        data-navigation-expanded={expandedNavigation()}
        data-native-sidebar={platform.platform === "desktop" && platform.os === "macos" ? "macos" : undefined}
        data-titlebar-position={bottomTitlebar() ? "bottom" : "top"}
        class="relative bg-v2-background-bg-deep flex-1 min-h-0 min-w-0 flex flex-col select-none [&_input]:select-text [&_textarea]:select-text [&_[contenteditable]]:select-text"
        style={{
          // Native Windows chrome supplies the gap; retain paint clearance for the panels' outer outlines.
          "--shell-top-inset": bottomTitlebar()
            ? "max(0px, calc(8px - env(safe-area-inset-top, 0px)))"
            : platform.platform === "desktop" && platform.os === "windows"
              ? "1px"
              : "0px",
          "--shell-bottom-inset": bottomTitlebar()
            ? "8px"
            : "max(0px, calc(8px - var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px))))",
        }}
      >
        <Titlebar
          update={update}
          navigation={{
            mount: state.navigationMount,
            expanded: expandedNavigation(),
            compact: compactNavigation(),
            onToggle: () => preferences.appearance.setSessionSidebar(!preferences.appearance.sessionSidebar()),
          }}
        />
        <div data-slot="sair-workbench-body" class="flex flex-1 min-h-0 min-w-0 flex-row">
          <aside data-slot="sair-navigation-mount">
            <div data-slot="sair-native-controls-space" aria-hidden="true" />
            <div data-slot="sair-navigation-content" ref={(element) => setState("navigationMount", element)} />
          </aside>
          {/* Size containment collapses percentage-height descendants in WebKit. */}
          <main
            class="flex-1 min-h-0 min-w-0 overflow-x-hidden flex flex-col items-start contain-content"
            style={{
              "padding-top": bottomTitlebar() ? "env(safe-area-inset-top, 0px)" : "0px",
              "padding-bottom":
                bottomTitlebar() || settings.active()
                  ? "0px"
                  : "var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px))",
              "--settings-bottom-inset": bottomTitlebar()
                ? "40px"
                : "var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px))",
              "--settings-top-inset": mobile() && !bottomTitlebar() ? "0px" : "var(--shell-top-inset, 8px)",
            }}
          >
            <SshAuthentication>
              <Suspense>{props.children}</Suspense>
            </SshAuthentication>
          </main>
        </div>
        <Show when={state.debugTools}>
          <Suspense>
            <DebugBar diagnostics={import.meta.env.DEV} inline />
          </Suspense>
        </Show>
        <ToastRegion />
        <UploadToastHost />
      </div>
    </TitlebarRightProvider>
  )
}
